import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, HandCoins, Gavel, Receipt, Download, AlertTriangle, Activity } from "lucide-react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";

const fmt = (n) => `₺${(Number(n) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "-");
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");
const isGecikti = (d) => { if (!d) return false; const t = new Date(d); const today = new Date(); today.setHours(0, 0, 0, 0); return t < today; };

const ROL_ETIKET = { admin: "Yönetici", user: "Kullanıcı", avukat: "Avukat", katip: "Katip" };
const RolBadge = ({ rol }) => (
  <span className="px-1.5 py-0.5 rounded-sm text-xs bg-[#EFE7D6] text-[#8C8377]">{ROL_ETIKET[rol] || rol || "-"}</span>
);

const AKSIYON_ETIKET = {
  login: "Giriş", create: "Oluşturma", edit: "Düzenleme", delete: "Silme", "kasa-import": "Kasa Yükleme",
  import: "İçe Aktarma", payment: "Tahsilat", "geri-al": "Geri Alma", "senet-ekle": "Senet Ekleme",
  "senet-toplu": "Toplu Senet", belge: "Belge", "cari-to-icra": "Cari→İcra", undo: "Geri Alma",
  "haciz-ekle": "Haciz Ekleme", "haciz-not": "Haciz Notu", "haciz-evrak": "Haciz Evrakı", role: "Rol",
  icralik: "İcralık", duzenle: "Düzenleme", durum: "Durum", approve: "Onay", reject: "Ret",
  gorusme: "Borçlu Görüşmesi",
};
const akLabel = (a) => AKSIYON_ETIKET[a] || a || "-";

export default function Denetim() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("odeme");
  const [rol, setRol] = useState("all");

  // İşlem Akışı
  const [akis, setAkis] = useState(null);
  const [akisLoading, setAkisLoading] = useState(false);
  const [fEmail, setFEmail] = useState("");
  const [fStart, setFStart] = useState("");
  const [fEnd, setFEnd] = useState("");

  useEffect(() => {
    api.get("/denetim/personel-islemleri")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Denetim listesi yüklenemedi"))
      .finally(() => setLoading(false));
  }, []);

  const loadAkis = () => {
    setAkisLoading(true);
    const params = {};
    if (fEmail) params.email = fEmail;
    if (fStart) params.start = fStart;
    if (fEnd) params.end = fEnd;
    api.get("/audit/activity", { params })
      .then(({ data }) => setAkis(data))
      .catch(() => toast.error("İşlem akışı yüklenemedi"))
      .finally(() => setAkisLoading(false));
  };
  useEffect(() => { if (tab === "akis" && !akis) loadAkis(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const s = data?.sayilar || {};
  const applyRol = (arr) => (arr || []).filter((r) => rol === "all" || r.rol === rol);
  const odeme = applyRol(data?.odeme_sozleri);
  const haciz = applyRol(data?.haciz_islemleri);
  const masraf = applyRol(data?.masraflar);

  const downloadTutanak = async (rid, evrakId, name) => {
    if (!rid || !evrakId) { toast.error("Tutanak bulunamadı"); return; }
    try {
      const res = await api.get(`/records/${rid}/haciz-evrak/${evrakId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = name || "haciz-tutanak";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("İndirilemedi"); }
  };

  const TabBtn = ({ id, label, Icon, count }) => (
    <button onClick={() => setTab(id)} data-testid={`denetim-tab-${id}`}
      className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium border transition-colors duration-150 ${tab === id ? "border-[#26231E] bg-[#26231E] text-white" : "border-[#E7DECC] bg-white text-[#8C8377] hover:bg-[#EFE7D6]"}`}>
      <Icon className="w-4 h-4" /> {label}
      <span className={`ml-1 px-1.5 py-0.5 rounded-sm text-xs ${tab === id ? "bg-white/20" : "bg-[#EFE7D6] text-[#8C8377]"}`}>{count ?? 0}</span>
    </button>
  );

  const Th = ({ children, right }) => <th className={`px-3 py-2.5 kicker ${right ? "text-right" : "text-left"}`}>{children}</th>;
  const Empty = ({ cols, text }) => <tr><td colSpan={cols} className="px-3 py-10 text-center text-[#8C8377]" data-testid="denetim-empty">{text}</td></tr>;

  return (
    <Layout>
      <div data-testid="denetim-page">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-6 h-6 text-[#B8933F]" />
          <h1 className="font-heading text-2xl font-bold text-[#26231E]">Personel İşlem Denetimi</h1>
        </div>
        <p className="text-sm text-[#8C8377] mb-5">Tüm personelin (yönetici/kullanıcı/avukat/katip) aldığı ödeme sözleri, haciz işlemleri ve girdiği masraflar. (Silme yalnız yöneticide.)</p>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <TabBtn id="odeme" label="Ödeme Sözleri" Icon={HandCoins} count={s.odeme_sozleri} />
          <TabBtn id="haciz" label="Haciz İşlemleri" Icon={Gavel} count={s.haciz_islemleri} />
          <TabBtn id="masraf" label="Masraflar" Icon={Receipt} count={s.masraflar} />
          <TabBtn id="akis" label="İşlem Akışı" Icon={Activity} count={akis?.toplam} />
          {tab !== "akis" && (
          <select value={rol} onChange={(e) => setRol(e.target.value)} data-testid="denetim-rol-filter" className="ml-auto px-3 py-2 border border-[#E7DECC] rounded-sm text-sm bg-white">
            <option value="all">Tüm Personel</option>
            <option value="admin">Yönetici</option>
            <option value="user">Kullanıcı</option>
            <option value="avukat">Avukat</option>
            <option value="katip">Katip</option>
          </select>
          )}
        </div>

        {tab === "akis" ? (
          <div data-testid="denetim-akis">
            <div className="flex flex-wrap items-end gap-3 mb-4 p-4 bg-white border border-[#E7DECC] rounded-sm">
              <div>
                <label className="kicker block mb-1">PERSONEL</label>
                <select value={fEmail} onChange={(e) => setFEmail(e.target.value)} data-testid="akis-email-filter" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm bg-white min-w-[200px]">
                  <option value="">Tüm Personel</option>
                  {(akis?.personeller || []).map((e) => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div>
                <label className="kicker block mb-1">BAŞLANGIÇ</label>
                <input type="date" value={fStart} onChange={(e) => setFStart(e.target.value)} data-testid="akis-start" className="px-2 py-2 border border-[#E7DECC] rounded-sm text-sm" />
              </div>
              <div>
                <label className="kicker block mb-1">BİTİŞ</label>
                <input type="date" value={fEnd} onChange={(e) => setFEnd(e.target.value)} data-testid="akis-end" className="px-2 py-2 border border-[#E7DECC] rounded-sm text-sm" />
              </div>
              <button onClick={loadAkis} disabled={akisLoading} data-testid="akis-apply" className="px-4 py-2 bg-[#26231E] text-white rounded-sm text-sm font-medium hover:bg-[#3a352d] disabled:opacity-50">{akisLoading ? "Yükleniyor…" : "Filtrele"}</button>
              <button onClick={() => { setFEmail(""); setFStart(""); setFEnd(""); setTimeout(loadAkis, 0); }} className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm text-[#8C8377] hover:text-[#26231E]">Temizle</button>
              <span className="ml-auto text-sm text-[#8C8377]">{akis?.toplam || 0} işlem</span>
            </div>
            <div className="bg-white border border-[#E7DECC] rounded-sm overflow-hidden">
              <div className="max-h-[560px] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#F3ECDD] border-b border-[#E7DECC] z-10"><tr>
                    <Th>Tarih-Saat</Th><Th>Personel</Th><Th>Rol</Th><Th>İşlem</Th><Th>Modül</Th><Th>Kayıt</Th><Th>Detay</Th>
                  </tr></thead>
                  <tbody>
                    {akisLoading ? (
                      <tr><td colSpan={7} className="px-3 py-10 text-center text-[#8C8377]">Yükleniyor...</td></tr>
                    ) : (akis?.logs || []).length === 0 ? (
                      <Empty cols={7} text="İşlem bulunamadı." />
                    ) : akis.logs.map((r) => (
                      <tr key={r.id} className="border-b border-[#EFE7D6] hover:bg-[#F3ECDD]" data-testid="akis-row">
                        <td className="px-3 py-2 tabular-nums whitespace-nowrap">{fmtDateTime(r.at)}</td>
                        <td className="px-3 py-2 font-medium">{r.by_name || r.by_email || "-"}</td>
                        <td className="px-3 py-2"><RolBadge rol={r.rol} /></td>
                        <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded-sm text-xs font-semibold bg-[#B8933F]/10 text-[#B8933F]">{akLabel(r.action)}</span></td>
                        <td className="px-3 py-2">{r.entity || "-"}</td>
                        <td className="px-3 py-2">{r.label && r.label !== "-" ? r.label : "-"}</td>
                        <td className="px-3 py-2 max-w-[320px] truncate text-[#8C8377]" title={r.detail}>{r.detail || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
        <div className="bg-white border border-[#E7DECC] rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            {loading ? (
              <tbody><tr><td className="px-3 py-10 text-center text-[#8C8377]">Yükleniyor...</td></tr></tbody>
            ) : tab === "odeme" ? (
              <>
                <thead className="bg-[#F3ECDD] border-b border-[#E7DECC]"><tr>
                  <Th>Kaynak</Th><Th>Borçlu</Th><Th>Dosya No</Th><Th>Mahiyet</Th><Th>Taahhüt Tarihi</Th><Th right>Taahhüt Tutarı</Th><Th>Açıklama</Th><Th>Ekleyen</Th><Th>Rol</Th><Th>Eklenme</Th><Th>Düzenlenme (Gün-Saat)</Th>
                </tr></thead>
                <tbody>
                  {odeme.length === 0 ? <Empty cols={11} text="Ödeme sözü bulunamadı." /> : odeme.map((r, i) => (
                    <tr key={i} className="border-b border-[#EFE7D6] hover:bg-[#F3ECDD]" data-testid={`denetim-odeme-${i}`}>
                      <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded-sm text-xs font-semibold ${r.kaynak === "İcra" ? "bg-[#A63A3A]/10 text-[#A63A3A]" : "bg-[#B8933F]/10 text-[#B8933F]"}`}>{r.kaynak}</span></td>
                      <td className="px-3 py-2 font-medium">{r.borclu}</td>
                      <td className="px-3 py-2">{r.dosya_no || "-"}</td>
                      <td className="px-3 py-2">{r.mahiyet || "-"}</td>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap">{isGecikti(r.taahhut_tarihi) && <AlertTriangle className="w-3.5 h-3.5 text-[#A63A3A] inline mr-1 -mt-0.5" />}{fmtDate(r.taahhut_tarihi)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(r.tutar)}</td>
                      <td className="px-3 py-2 max-w-[220px] truncate" title={r.aciklama}>{r.aciklama || "-"}</td>
                      <td className="px-3 py-2">{r.ekleyen_ad || r.ekleyen_email || "-"}</td>
                      <td className="px-3 py-2"><RolBadge rol={r.rol} /></td>
                      <td className="px-3 py-2 tabular-nums text-[#8C8377]">{fmtDate(r.eklenme_tarihi)}</td>
                      <td className="px-3 py-2 tabular-nums">{r.duzenlendi_at ? <span className="text-[#B8933F] font-medium">{fmtDateTime(r.duzenlendi_at)}{r.duzenleyen_ad ? ` · ${r.duzenleyen_ad}` : ""}</span> : <span className="text-[#8C8377]">-</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </>
            ) : tab === "haciz" ? (
              <>
                <thead className="bg-[#F3ECDD] border-b border-[#E7DECC]"><tr>
                  <Th>Tip</Th><Th>Borçlu</Th><Th>Dosya No</Th><Th>İçerik / Belge</Th><Th>Ekleyen</Th><Th>Rol</Th><Th>Eklenme</Th><Th right>İndir</Th>
                </tr></thead>
                <tbody>
                  {haciz.length === 0 ? <Empty cols={8} text="Haciz işlemi bulunamadı." /> : haciz.map((r, i) => (
                    <tr key={i} className="border-b border-[#EFE7D6] hover:bg-[#F3ECDD]" data-testid={`denetim-haciz-${i}`}>
                      <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded-sm text-xs font-semibold ${r.tip === "Haciz Tutanağı" ? "bg-[#2F6E4E]/10 text-[#2F6E4E]" : "bg-[#7C5E9B]/10 text-[#7C5E9B]"}`}>{r.tip}</span></td>
                      <td className="px-3 py-2 font-medium">{r.borclu}</td>
                      <td className="px-3 py-2">{r.dosya_no || "-"}</td>
                      <td className="px-3 py-2 max-w-[300px] truncate" title={r.aciklama}>{r.aciklama || "-"}</td>
                      <td className="px-3 py-2">{r.ekleyen_ad || r.ekleyen_email || "-"}</td>
                      <td className="px-3 py-2"><RolBadge rol={r.rol} /></td>
                      <td className="px-3 py-2 tabular-nums text-[#8C8377]">{fmtDate(r.eklenme_tarihi)}</td>
                      <td className="px-3 py-2 text-right">
                        {r.tip === "Haciz Tutanağı" && r.evrak_id ? (
                          <button onClick={() => downloadTutanak(r.record_id, r.evrak_id, r.aciklama)} data-testid={`denetim-haciz-dl-${i}`} title="Tutanağı İndir" className="p-1 text-[#8C8377] hover:text-[#B8933F] transition-colors duration-150"><Download className="w-4 h-4 inline" /></button>
                        ) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </>
            ) : tab === "masraf" ? (
              <>
                <thead className="bg-[#F3ECDD] border-b border-[#E7DECC]"><tr>
                  <Th>Borçlu</Th><Th>Dosya No</Th><Th>İşlem Tarihi</Th><Th right>Tutar</Th><Th>Açıklama</Th><Th>Ekleyen</Th><Th>Rol</Th><Th>Eklenme</Th>
                </tr></thead>
                <tbody>
                  {masraf.length === 0 ? <Empty cols={8} text="Masraf bulunamadı." /> : masraf.map((r, i) => (
                    <tr key={i} className="border-b border-[#EFE7D6] hover:bg-[#F3ECDD]" data-testid={`denetim-masraf-${i}`}>
                      <td className="px-3 py-2 font-medium">{r.borclu}</td>
                      <td className="px-3 py-2">{r.dosya_no || "-"}</td>
                      <td className="px-3 py-2 tabular-nums">{fmtDate(r.tarih)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#B8933F]">{fmt(r.tutar)}</td>
                      <td className="px-3 py-2 max-w-[240px] truncate" title={r.aciklama}>{r.aciklama || "-"}</td>
                      <td className="px-3 py-2">{r.ekleyen_ad || r.ekleyen_email || "-"}</td>
                      <td className="px-3 py-2"><RolBadge rol={r.rol} /></td>
                      <td className="px-3 py-2 tabular-nums text-[#8C8377]">{fmtDate(r.eklenme_tarihi)}</td>
                    </tr>
                  ))}
                </tbody>
              </>
            ) : null}
          </table>
        </div>
        )}
      </div>
    </Layout>
  );
}
